import React from 'react';
import { formatMoney } from '@dill/dill-shared';
import { DillButton, DillModal } from '../Dill';
import { ScheduledPaymentWithNames, formatScheduledDate } from './scheduledPaymentDisplay';

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-start gap-3 text-xs">
    <span className="text-slate-400 shrink-0">{label}</span>
    <span className="ml-auto text-right text-slate-800 break-words">{value}</span>
  </div>
);

/**
 * Everything captured on a scheduled payment, for whichever side is looking at it.
 *
 * Read-only: rescheduling and cancelling stay on the row in the list, so this stays a single-purpose
 * "what exactly is going to happen" view for both the buyer who queued the payment and the supplier
 * expecting it.
 */
export const ScheduledPaymentDetailsModal = ({
  scheduledPayment,
  counterpartyLabel,
  counterpartyName,
  onClose,
}: {
  /** The payment to show; `null` keeps the modal closed. */
  scheduledPayment: ScheduledPaymentWithNames | null;
  counterpartyLabel: string;
  counterpartyName: string;
  onClose: () => void;
}) => {
  const invoicePayments = scheduledPayment?.invoicePayments ?? [];
  const skippedInvoiceIds = scheduledPayment?.skippedInvoiceIds ?? [];

  return (
    <DillModal
      open={Boolean(scheduledPayment)}
      onClose={onClose}
      title="Scheduled Payment"
      subtitle={counterpartyName}
      maxWidth="520px"
      contentClassName="px-5 py-4 flex flex-col gap-4"
      footer={<DillButton label="Close" variant="secondary" onClick={onClose} />}>
      {scheduledPayment && (
        <>
          <div className="flex flex-col gap-1.5">
            <DetailRow label={counterpartyLabel} value={counterpartyName} />
            <DetailRow
              label="Runs on"
              value={formatScheduledDate(scheduledPayment.scheduledDate, 'dddd, MMMM D, YYYY')}
            />
            <DetailRow label="Status" value={scheduledPayment.status} />
            <DetailRow label="Total" value={formatMoney(scheduledPayment.totalAmount, 2)} />
            {Boolean(scheduledPayment.totalCreditsToUse) && (
              <DetailRow
                label="Credits applied"
                value={formatMoney(scheduledPayment.totalCreditsToUse || 0, 2)}
              />
            )}
            {scheduledPayment.partialPaymentReason && (
              <DetailRow label="Reason" value={scheduledPayment.partialPaymentReason} />
            )}
            {scheduledPayment.partialPaymentNotes && (
              <DetailRow label="Notes" value={scheduledPayment.partialPaymentNotes} />
            )}
            {scheduledPayment.failureReason && (
              <DetailRow label="Note" value={scheduledPayment.failureReason} />
            )}
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-700 mb-2">
              {invoicePayments.length} invoice{invoicePayments.length === 1 ? '' : 's'}
            </div>
            <div className="flex flex-col divide-y divide-slate-100 rounded-lg border border-slate-100">
              {invoicePayments.map((invoicePayment, index) => {
                const invoiceId =
                  invoicePayment.providerInvoiceId || invoicePayment.quickbooksInvoiceId || '';
                const isSkipped = skippedInvoiceIds.includes(invoiceId);
                return (
                  <div
                    key={`${scheduledPayment.id}-${invoiceId || index}`}
                    className="flex items-center gap-3 px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-800 truncate">
                        {invoicePayment.invoiceNumber}
                      </div>
                      <div className="text-2xs text-slate-400 mt-0.5">
                        {invoicePayment.dueDate
                          ? `Due ${formatScheduledDate(invoicePayment.dueDate, 'MM/DD/YYYY')}`
                          : 'No due date'}
                        {Boolean(invoicePayment.openAmount) &&
                          ` · ${formatMoney(invoicePayment.openAmount || 0, 2)} open`}
                        {isSkipped && ' · will be skipped'}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs font-bold text-slate-900">
                        {formatMoney(invoicePayment.amountPaid, 2)}
                      </div>
                      {Boolean(invoicePayment.discountAmount) && (
                        <div className="text-2xs text-emerald-600">
                          −{formatMoney(invoicePayment.discountAmount || 0, 2)} discount
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </DillModal>
  );
};
